import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../assets/landing.css";
import "../assets/game.css";

const Game = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [started, setStarted] = useState(null);
  const [initialTime, setInitialTime] = useState(10);

  const API_URL = process.env.REACT_APP_API_URL;
  const ALPHA = ["A", "B", "C", "D", "E", "F"];

  useEffect(() => {
    fetch(`${API_URL}/games/categories`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => setCategories(data))
      .catch((error) => console.error("Error fetching game categories:", error));
  }, [API_URL]);

  useEffect(() => {
    if (selectedGame && started) {
      setTimeLeft(initialTime);
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            revealCorrectAnswer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentQuestionIndex, selectedGame, started, initialTime]);

  const startGame = () => setStarted(true);

  const selectCategory = (file) => {
    fetch(`${API_URL}/game/${file}`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        setSelectedCategory(data.levels);
        setGames(data);
      })
      .catch((error) => console.error("Error fetching games:", error));
  };

  const selectGame = (game) => {
    setSelectedGame(game);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const checkAnswer = (answer) => {
    if (selectedAnswer) return;
    setSelectedAnswer(answer.answer);
    setFeedback(answer.correct);
  };

  const revealCorrectAnswer = () => {
    if (!selectedAnswer) {
      const correctAnswer = selectedGame.questions[currentQuestionIndex].answers.find(
        (a) => a.correct
      );
      setSelectedAnswer(correctAnswer.answer);
      setFeedback(true);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < selectedGame.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setFeedback(null);
      setTimeLeft(initialTime);
      setStarted(true);
    } else {
      alert("Game over!");
      setStarted(false);
      setSelectedGame(null);
    }
  };

  const resetToCategories = () => {
    setSelectedCategory(null);
    setSelectedGame(null);
    setGames([]);
    setStarted(null);
  };

  const getAnswerClass = (answer) => {
    if (selectedAnswer !== answer.answer) return "";
    return feedback ? "game-answer--selected-correct" : "game-answer--selected-wrong";
  };

  const enabledCategories = categories.filter(
    (c) => c.enabled === true && c.name !== "Unknown"
  );

  const enabledLevels = games.levels?.filter(
    (g) => g.enabled === true && g.name !== "Unknown"
  ) ?? [];

  const timerUrgent = started && timeLeft <= 3;

  return (
    <div className="game-page">
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-logo">
            <span className="landing-logo-icon">?</span>
            <span>
              Trivia<span className="landing-logo-accent">Quest</span>
            </span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/">Home</Link>
          </div>
        </div>
      </nav>

      <main className="game-main">
        {!selectedCategory ? (
          <>
            <header className="game-header">
              <p className="game-eyebrow">Step 1</p>
              <h1 className="game-title">Select a Category</h1>
              <p className="game-subtitle">
                Choose a topic to test your financial literacy knowledge.
              </p>
            </header>
            <div className="game-card">
              {enabledCategories.length === 0 ? (
                <p className="game-empty">Loading categories…</p>
              ) : (
                <div className="game-category-grid">
                  {enabledCategories.map((category) => (
                    <button
                      key={category.name}
                      type="button"
                      className="game-category-btn"
                      onClick={() => selectCategory(category.file)}
                    >
                      <h2>{category.name}</h2>
                      <p>{category.description}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : !selectedGame ? (
          <>
            <header className="game-header">
              <p className="game-eyebrow">Step 2</p>
              <h1 className="game-title">Choose Your Level</h1>
              <p className="game-subtitle">{games.name}</p>
            </header>
            <div className="game-settings">
              <label htmlFor="countDown">Timer (seconds)</label>
              <input
                type="number"
                name="countDown"
                id="countDown"
                min="5"
                max="60"
                value={initialTime}
                onChange={(e) => setInitialTime(Number(e.target.value))}
              />
            </div>
            <button type="button" className="game-text-link" onClick={resetToCategories}>
              ← Change category
            </button>
            <div className="game-card">
              <div className="game-level-list">
                {enabledLevels.map((game) => (
                  <button
                    key={game.name}
                    type="button"
                    className="game-level-btn"
                    onClick={() => selectGame(game)}
                  >
                    <h2>{game.name}</h2>
                    <p>{game.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <header className="game-header">
              <p className="game-eyebrow">In Progress</p>
              <h1 className="game-title">{selectedGame.name}</h1>
            </header>
            <div className="game-card game-play-card">
              <div className="game-play-header">
                <p className="game-play-meta">
                  Question {currentQuestionIndex + 1} of {selectedGame.questions.length}
                </p>
              </div>

              {started && (
                <>
                  <div className={`game-timer${timerUrgent ? " game-timer--urgent" : ""}`}>
                    {timeLeft}
                  </div>
                  <div className="game-progress">
                    <div
                      className="game-progress-bar"
                      style={{ width: `${(timeLeft / initialTime) * 100}%` }}
                    />
                  </div>
                </>
              )}

              <p className="game-question">
                {selectedGame.questions[currentQuestionIndex].question}
              </p>

              <div className="game-answers">
                {selectedGame.questions[currentQuestionIndex].answers.map((answer, idx) => (
                  <button
                    key={answer.answer}
                    type="button"
                    className={`game-answer ${getAnswerClass(answer)}${
                      selectedAnswer ? " game-answer--locked" : ""
                    }`}
                    onClick={() => checkAnswer(answer)}
                    disabled={!!selectedAnswer}
                  >
                    <span className="game-answer-letter">{ALPHA[idx]}.</span>
                    <span>{answer.answer}</span>
                  </button>
                ))}
              </div>

              {feedback && (
                <div className="game-comment">
                  {selectedGame.questions[currentQuestionIndex].comment}
                </div>
              )}
            </div>

            <div className="game-actions">
              {started ? (
                <button
                  type="button"
                  className="game-btn game-btn--primary"
                  disabled={!feedback}
                  onClick={nextQuestion}
                >
                  {currentQuestionIndex < selectedGame.questions.length - 1
                    ? "Next Question →"
                    : "Finish Game →"}
                </button>
              ) : (
                <button
                  type="button"
                  className="game-btn game-btn--primary"
                  onClick={startGame}
                >
                  Start Game
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Game;
